import { Table } from '@data/components/basic/Table/Table';
import { ReportHighlights } from '@data/components/report/ReportHighlights/ReportHighlights';
import { useReportOutletContext } from '@data/pages/ReportLayout/ReportLayout';
import { resolveMetrics, type MetricRule } from '@data/utils/resolve-metrics';
import { type ColumnRule } from '@data/utils/resolve-columns';
import InfoIcon from '@data/assets/bold-circle-info.svg';
import {
  MetricTableType,
  useMetricsTableV2Api,
} from '@data/hooks/api/use-metrics-table-v2-api/use-metrics-table-v2-api';
import { useExportToCSVHandler } from '@data/hooks/use-report-tables/use-export-to-csv-handler/use-export-to-csv-handler';
import { useResetTableOnFilterChange } from '@data/hooks/use-report-tables/use-reset-table-on-filter-change/use-reset-table-on-filter-change';
import { useTableControls } from '@data/hooks/use-report-tables/use-table-controls/use-table-controls';
import { buildOrderBy } from '@data/utils/build-order-by';
import { filterTableData } from '@data/utils/filter-table-data';
import { useGetMeApi } from 'eds-common/api/v2';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { NON_MAGAZINE_PRODUCTS as NON_MAGAZINE_ED_APPS } from '@data/config/reports';
import { getEdApps } from '@data/utils/get-ed-apps';
import { Popover } from '@scholastic/mockingjay';

// ── Engagement Feed Config ────────────────────────────────────────────

const PRIMARY_METRICS: MetricRule[] = [
  { metric: 'CONTENT_VIEWS' },
  { metric: 'TOTAL_MINUTES_READING', products: ['LITPRO', 'R4R'] },
  { metric: 'ACTIVITIES_COMPLETED' },
  { metric: 'AVG_ACTIVITY_SCORE' },
];

const ENGAGEMENT_FEED_TABLE_COLUMNS: ColumnRule[] = [
  { key: 'RESOURCE_NAME' },
  { key: 'LEXILE_MEASURE', edApps: ['LITPRO'] },
  { key: 'TOTAL_READING_MINUTES', edApps: ['LITPRO', 'R4R'] },
  { key: 'INTERACTIONS' },
  { key: 'ACTIVITIES_COMPLETED' },
  { key: 'AVG_ACTIVITY_SCORE' },
  { key: 'DATE_LAST_ACCESSED' },
];

// ── Component ──────────────────────────────────────────────────────────────────

export function EngagementFeedPage() {
  const {
    dateRange,
    drilldown,
    productId: edApp,
    reportPath,
  } = useReportOutletContext();
  const { data: user } = useGetMeApi();
  const [searchParams] = useSearchParams();
  const classIds = searchParams.getAll('classId');
  const studentIds = searchParams.getAll('studentId');

  // ── Metrics ──────────────────────────────────────────────────────────────────

  const primaryMetricTypes = resolveMetrics(PRIMARY_METRICS, edApp, drilldown);

  // ── Table ──────────────────────────────────────────────────────────────────

  const showTable = (NON_MAGAZINE_ED_APPS as string[]).includes(edApp);

  const { tableProps, searchTerms, userSorted, reset } = useTableControls({
    defaultSortColumn: 'DATE_LAST_ACCESSED',
    defaultSortDirection: 'desc',
  });

  useResetTableOnFilterChange(
    drilldown,
    classIds.join(','),
    studentIds.join(','),
    reset
  );

  const sharedMetricsArgs = {
    orgId: user?.currentOrg?.orgId ?? '',
    metricType: MetricTableType.EngagementFeed,
    teacherIds: [user?.userId ?? ''],
    startDate: dateRange.from,
    endDate: dateRange.to,
    edApps: getEdApps(edApp),
    classIds: classIds.length > 0 ? classIds : null,
    studentIds: studentIds.length > 0 ? studentIds : null,
    pageSize: tableProps.pageSize,
    ...(searchTerms ? { searchTerms } : {}),
    ...buildOrderBy(
      tableProps.sortColumn,
      tableProps.sortDirection,
      searchTerms,
      userSorted
    ),
  };

  const {
    data: tableData,
    loading: tableMetricsLoading,
    error,
  } = useMetricsTableV2Api({
    ...sharedMetricsArgs,
    page: tableProps.page,
    enabled:
      showTable && !!user?.userId && !!user?.currentOrg?.orgId && !!edApp,
  });

  const { refetch: refetchExportData } = useMetricsTableV2Api({
    ...sharedMetricsArgs,
    page: 1,
    fetchAllRecords: true,
    enabled: false,
  });

  const { visibleColumns, visibleRows } = useMemo(
    () => filterTableData(tableData, ENGAGEMENT_FEED_TABLE_COLUMNS, edApp),
    [tableData, edApp]
  );

  const handleExportToCSV = useExportToCSVHandler(
    refetchExportData,
    ENGAGEMENT_FEED_TABLE_COLUMNS,
    edApp,
    reportPath
  );

  return (
    <div className="flex flex-col gap-6">
      <ReportHighlights primaryMetricTypes={primaryMetricTypes} />
      {showTable && (
        <>
          <div className="flex flex-row items-center gap-2">
            <span className="text-xl font-bold text-neutral-1300 tablet:text-2xl">
              Details
            </span>
            {tableData?.toolTips && (
              <Popover
                trigger={
                  <button
                    aria-label={`More information about ${tableData?.name}`}
                    type="button"
                    className="flex items-center"
                  >
                    <InfoIcon aria-hidden="true" className="h-6 w-6" />
                  </button>
                }
              >
                {tableData.toolTips}
              </Popover>
            )}
          </div>
          <Table
            {...tableProps}
            loading={tableMetricsLoading}
            columns={visibleColumns}
            rows={visibleRows}
            totalCount={tableData?.totalRecords ?? 0}
            onExportToCSV={handleExportToCSV}
            error={error}
          />
        </>
      )}
    </div>
  );
}
